import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CategorySidebar from "./components/CategorySidebar";
import FilterBar from "./components/FilterBar";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/base/Pagination";
import { getCategoryById, getVideosByCategory } from "@/utils/video";
import { regions, years, sortOptions } from "@/mocks/videos";
import type { Video } from "@/types/video";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);

  const [activeSub, setActiveSub] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState(sortOptions[0]);
  const [page, setPage] = useState(1);

  useDocumentMeta(
    category ? `${category.name} - 西瓜视频分类频道` : "分类 - 西瓜视频",
    category
      ? `西瓜视频「${category.name}」频道：${category.desc} 海量高清内容在线观看，支持按地区、年份与热度筛选排序。`
      : "西瓜视频分类频道，提供丰富多样的视频内容。",
    category
      ? `${category.name},西瓜视频,${category.name}视频,在线观看,视频分类`
      : "西瓜视频,视频分类"
  );

  useEffect(() => {
    setActiveSub("全部");
    setRegion("全部");
    setYear("全部");
    setSort(sortOptions[0]);
    setPage(1);
  }, [categoryId]);

  useEffect(() => {
    setPage(1);
  }, [activeSub, region, year, sort]);

  const filtered = useMemo<Video[]>(() => {
    if (!category) return [];
    let list = getVideosByCategory(category.id);
    if (activeSub !== "全部") list = list.filter((v) => v.subcategory === activeSub);
    if (region !== "全部") list = list.filter((v) => v.region === region);
    if (year !== "全部") list = list.filter((v) => v.year === year);

    const sorted = [...list];
    if (sort === "最多播放") sorted.sort((a, b) => b.plays - a.plays);
    else if (sort === "最新发布")
      sorted.sort(
        (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
      );
    return sorted;
  }, [category, activeSub, region, year, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleReset = () => {
    setActiveSub("全部");
    setRegion("全部");
    setYear("全部");
    setSort(sortOptions[0]);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
        </span>
        <h1 className="text-lg font-bold text-foreground-900 mb-2">分类不存在</h1>
        <p className="text-sm text-foreground-500 mb-6">你访问的分类可能已被移除或链接有误。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-home-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-300"></i>
        <span className="text-foreground-800">{category.name}</span>
      </nav>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <CategorySidebar activeId={category.id} />

        <div className="flex-1 min-w-0">
          <div className="mb-5">
            <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-1.5">{category.desc}</p>
          </div>

          <FilterBar
            subcategories={category.subcategories}
            activeSub={activeSub}
            onSubChange={setActiveSub}
            region={region}
            onRegionChange={setRegion}
            year={year}
            onYearChange={setYear}
            sort={sort}
            onSortChange={setSort}
            onReset={handleReset}
          />

          <p className="text-sm text-foreground-500 mb-4">
            共找到 <span className="text-primary-600 font-medium">{filtered.length}</span> 个视频
          </p>

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {pageItems.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <div className="py-20 text-center">
              <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-4">
                <i className="ri-movie-2-line text-3xl text-foreground-400"></i>
              </span>
              <h2 className="text-base font-bold text-foreground-900 mb-2">暂无相关视频</h2>
              <p className="text-sm text-foreground-500 mb-6">
                换个筛选条件试试，或重置筛选查看全部内容。
              </p>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-1 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}